import { Spacing } from '@persona-id/design-tokens';
import {
  BookOpenIcon,
  BriefcaseIcon,
  CircleAlertIcon,
  GlobeIcon,
  SocialFacebookIcon,
  SocialGithubIcon,
  SocialLinkedinIcon,
  SocialTwitterIcon,
  UserIcon,
} from '@persona-id/design-tokens/icons';
import styled, { css } from 'styled-components';

import { AttributeToggle } from 'dashboard/components/ReportResult/Profile/Profile';
import ReportResultLayout from 'dashboard/components/ReportResult/ReportResultLayout';
import Banner from 'dashboard/components-lib/Banner';
import TableLayout, { TableTD, TableTH } from 'dashboard/layouts/TableLayout';
import { formatReportDates } from 'dashboard/lib/utils/formatters';
import { capitalizeSentence } from 'dashboard/lib/utils/stringFormat';
import {
  ProfileNonAuthoritativeLocation,
  ReportProfileNonAuthoritative,
} from 'dashboard/models/report/profile-non-authoritative';
import Card from 'design-system/components/Card';
import { MatchHighlight } from 'design-system/components/MatchHighlight';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { Paragraph } from 'design-system/components/Text';
import { isFocused } from 'design-system/lib/pseudoClass';
import { focusMixin } from 'design-system/mixins/hoverable';
import { TransitionDuration } from 'design-system/variables/transitions';

interface ProfileNonAuthoritativeProps {
  report: ReportProfileNonAuthoritative;
  multiCardFormat: boolean;
  isRedacted: boolean;
}

const DESIRED_FORMAT_EXP = 'MMM y';
const DESIRED_FORMAT_EDU = 'y';

const ensureScheme = (url: string) => {
  // Add href component if it doesn't exist already
  if (url.startsWith('https')) {
    return url;
  }

  return `https://${url}`;
};

const Section = styled.section`
  padding: ${Spacing[4]};

  & > h4 {
    margin-bottom: ${Spacing[3]};
    display: flex;
    align-items: center;
    grid-gap: ${Spacing[1]};
  }

  h4 {
    border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
  }
`;

const InfoContent = styled.dl`
  display: grid;
  grid-gap: ${Spacing[2]};
  grid-template-columns: repeat(2, 1fr);

  & dt {
    color: ${({ theme }) => `${theme.color.text.secondary}`};
  }
`;

const TableSection = styled(Section)`
  tr {
    &:focus {
      color: ${({ theme }) => theme.color.primary.brand};
      background-color: ${({ theme }) => theme.color.primary.brand100};
      cursor: pointer;
    }

    svg {
      color: ${({ theme }) => theme.color.primary.brand};
      background-color: ${({ theme }) => theme.color.primary.brand100};
      visibility: hidden;
      margin-left: 8px;
    }
  }
`;

const TableRow = styled.tr.attrs({ role: 'row' })`
  display: table-row;

  ${({ onClick }) =>
    onClick != null &&
    css`
      cursor: pointer;
    `}
  &${isFocused} {
    ${focusMixin()}
  }
`;

const TableTR = styled(TableRow)`
  ${({ onClick }) =>
    onClick != null &&
    css`
      cursor: pointer;
      transition-duration: ${TransitionDuration.Fast};
    `}
`;

const ScrollableDetails = styled.dd`
  ul {
    max-height: 170px;
  }
`;

const FlexContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing[4]}; // 16px
`;

const FlexRow = styled.div`
  display: flex;
  align-items: center;
`;

const IconWrapper = styled.div`
  width: ${Spacing[5]}; // 24px
  height: ${Spacing[4]}; // 24px
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: ${Spacing[4]}; // 16px, to match the gap in the header
`;

const UsernameWrapper = styled.div`
  flex: 1;
`;

const getNetworkIcon = (network: string) => {
  switch (network.toLowerCase()) {
    case 'facebook':
      return <SocialFacebookIcon />;
    case 'linkedin':
      return <SocialLinkedinIcon />;
    case 'twitter':
      return <SocialTwitterIcon />;
    case 'github':
      return <SocialGithubIcon />;
    default:
      return <GlobeIcon />;
  }
};

function formatLocation(location: ProfileNonAuthoritativeLocation | null | undefined): string {
  if (!location) return '';

  const parts = ['locality', 'region', 'country']
    .map((part) => capitalizeSentence(location[part as keyof ProfileNonAuthoritativeLocation]))
    .filter(Boolean);

  return parts.join(', ');
}

const processDates = (inputStartDate: string, inputEndDate: string, desiredFormat: string) => {
  if (inputStartDate || inputEndDate) {
    return (
      (inputStartDate ? formatReportDates(inputStartDate, desiredFormat) : '') +
      ' - ' +
      (inputEndDate ? formatReportDates(inputEndDate, desiredFormat) : 'Present')
    );
  }
  return 'N/A';
};

const ProfileNonAuthoritative = ({
  report,
  multiCardFormat = false,
  isRedacted,
}: ProfileNonAuthoritativeProps) => {
  const result = report.attributes.result;

  const {
    name: name,
    birthdate: birthdate,
    emails: emails,
    'phone-numbers': phoneNumbers,
    location: location,
    education: education,
    experience: experience,
    'social-media-profiles': socialMediaProfiles,
  } = result ?? {};

  const content = isRedacted ? (
    <Banner icon={<CircleAlertIcon />}>This report has been redacted</Banner>
  ) : name ? (
    <>
      <Card.Title>Profile Non-Authoritative Information</Card.Title>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div style={{ width: '60%' }}>
          <>
            <Section>
              <Paragraph size='lg' weight='bold' color='primary' as='h4'>
                <Sticker icon={<UserIcon />} /> Profile
              </Paragraph>
              <InfoContent>
                <dt>Full name</dt>
                <dd>
                  <MatchHighlight>{name ? capitalizeSentence(name) : '-'}</MatchHighlight>
                </dd>
                <dt>Birthdate</dt>
                <MatchHighlight>{birthdate ? birthdate : 'N/A'}</MatchHighlight>
                <dt>Occupation</dt>
                <MatchHighlight>
                  {experience && experience.length > 0
                    ? capitalizeSentence(experience[0]['title-name'])
                    : ''}
                </MatchHighlight>
                <dt>Company</dt>
                <MatchHighlight>
                  {experience && experience.length > 0
                    ? capitalizeSentence(experience[0]['company-name'])
                    : ''}
                </MatchHighlight>
                <dt>Phone number</dt>
                <MatchHighlight>
                  <ScrollableDetails>
                    <AttributeToggle
                      key='phoneNumbers-full'
                      id='phoneNumbers-full'
                      items={phoneNumbers ?? []}
                    />
                  </ScrollableDetails>
                </MatchHighlight>
                <dt>Email</dt>
                <MatchHighlight>
                  <ScrollableDetails>
                    <AttributeToggle
                      key='emails-full'
                      id='emails-full'
                      items={emails?.map((email) => email['address']) || []}
                    />
                  </ScrollableDetails>
                </MatchHighlight>
                <dt>Location</dt>
                <MatchHighlight>{location ? formatLocation(location) : ''}</MatchHighlight>
              </InfoContent>
            </Section>
            <Spacer height={1} />
          </>
        </div>

        <div style={{ width: '30%' }}>
          <Section>
            <Paragraph size='lg' weight='bold' color='primary' as='h4'>
              <Sticker icon={<GlobeIcon />} /> Social Media
            </Paragraph>
            <FlexContainer>
              {socialMediaProfiles &&
                socialMediaProfiles.map((profile) => (
                  <FlexRow
                    key={profile['network']}
                    data-test={`social-media-accounts__row--${profile['network'].toLowerCase()}`}
                  >
                    <IconWrapper>{getNetworkIcon(profile['network'])}</IconWrapper>
                    <UsernameWrapper>
                      {profile['username'] ? (
                        <a target='_blank' rel='noreferrer' href={ensureScheme(profile['url'])}>
                          {profile['username']}
                        </a>
                      ) : (
                        '-'
                      )}
                    </UsernameWrapper>
                  </FlexRow>
                ))}
            </FlexContainer>
          </Section>
        </div>
      </div>

      <TableSection>
        <Paragraph size='lg' weight='bold' color='primary' as='h4'>
          <Sticker icon={<BriefcaseIcon />} /> Experience
        </Paragraph>
        <TableLayout
          dataTest='experience-table'
          isLoading={false}
          showHeader={true}
          isEmpty={experience && experience.length === 0}
          nullState={null}
          headerTR={
            <>
              <TableTH>Company</TableTH>
              <TableTH>Title </TableTH>
              <TableTH>Date</TableTH>
            </>
          }
        >
          {experience &&
            experience.map((e, index) => (
              <TableTR key={`experience-row-${index}`}>
                <TableTD>{capitalizeSentence(e['company-name'])}</TableTD>
                <TableTD>{capitalizeSentence(e['title-name'])}</TableTD>
                <TableTD>
                  {processDates(e['start-date'], e['end-date'], DESIRED_FORMAT_EXP)}
                </TableTD>
              </TableTR>
            ))}
        </TableLayout>
      </TableSection>

      <TableSection>
        <Paragraph size='lg' weight='bold' color='primary' as='h4'>
          <Sticker icon={<BookOpenIcon />} /> Education
        </Paragraph>
        <TableLayout
          dataTest='education-table'
          isLoading={false}
          showHeader={true}
          isEmpty={education && education.length === 0}
          nullState={null}
          headerTR={
            <>
              <TableTH>School</TableTH>
              <TableTH>Type</TableTH>
              <TableTH>Date</TableTH>
            </>
          }
        >
          {education &&
            education.map((e, index) => (
              <TableTR key={`education-row-${index}`}>
                <TableTD>{capitalizeSentence(e['school-name'])}</TableTD>
                <TableTD>{capitalizeSentence(e['school-type'])}</TableTD>
                <TableTD>
                  {processDates(e['start-date'], e['end-date'], DESIRED_FORMAT_EDU)}
                </TableTD>
              </TableTR>
            ))}
        </TableLayout>
      </TableSection>
    </>
  ) : (
    <div>No match found</div>
  );

  return <ReportResultLayout multiCardFormat={multiCardFormat}>{content}</ReportResultLayout>;
};

export default ProfileNonAuthoritative;
